export type AbbreviationMap = ReadonlyMap<string, string>;

export interface AbbreviationExpansionResult {
	value: string;
	cursorPosition: number;
	trigger: string;
}

export const ABBREVIATION_TRIGGER_ERROR =
	"Abbreviation must be 2-50 characters and cannot contain control characters";

const UNSUPPORTED_TRIGGER_CHARS = /[\p{Cc}\p{Cs}\p{Zl}\p{Zp}]/u;

// Invisible characters can never be reproduced by typing, so a trigger containing one
// would never match. ZWJ/ZWNJ are kept: they join emoji sequences and control letter
// forms in Arabic/Indic scripts. Must match INVISIBLE_CHARS in the backend exactly.
const INVISIBLE_CHARS =
	/[\u200B\u200E\u200F\uFEFF\u00AD\u034F\u061C\u2060-\u2064\u2066-\u206F]/g;

// Strip before trimming: an invisible character at the edge would otherwise shield
// the whitespace next to it from the trim.
export function normalizeAbbreviationTrigger(value: string): string {
	return value.replace(INVISIBLE_CHARS, "").trim().normalize("NFC");
}

export function isValidAbbreviationTrigger(value: string): boolean {
	const normalized = normalizeAbbreviationTrigger(value);
	return (
		normalized.length >= 2 &&
		normalized.length <= 50 &&
		!UNSUPPORTED_TRIGGER_CHARS.test(normalized)
	);
}

interface ExpandAbbreviationAtCursorOptions {
	value: string;
	previousValue: string;
	cursorPosition: number | null | undefined;
	abbreviationMap: AbbreviationMap | null | undefined;
}

const TRAILING_PUNCT = /[.,;:!?]+$/;
const POSSESSIVE = /['’]s?$/;

// A trigger must start the field or follow a non-word character, so "(atty" and
// "—atty" fire while "prefixC++" doesn't.
const WORD_CHARACTER = /[\p{L}\p{N}\p{M}]/u;

// Reads the whole code point, so an astral letter isn't mistaken for a boundary by
// its trailing surrogate alone.
function endsWithWordCharacter(text: string, end: number): boolean {
	if (end === 0) return false;
	const previous = text.charCodeAt(end - 1);
	const isLowSurrogate = previous >= 0xdc00 && previous <= 0xdfff;
	const start = isLowSurrogate && end >= 2 ? end - 2 : end - 1;
	return WORD_CHARACTER.test(text.slice(start, end));
}

interface TriggerMatch {
	trigger: string;
	replacement: string;
	start: number;
}

/**
 * Find the trigger ending at `text`, longest match winning. Triggers may contain
 * spaces and punctuation, so each is tested as a suffix rather than tokenized.
 */
function findTriggerAtEnd(
	text: string,
	abbreviationMap: AbbreviationMap,
	fold: (value: string) => string,
): TriggerMatch | null {
	const haystack = fold(text);
	let matchedTrigger: string | null = null;
	let replacement: string | undefined;

	for (const [trigger, candidateReplacement] of abbreviationMap) {
		if (!trigger || !haystack.endsWith(fold(trigger))) continue;

		const triggerStart = text.length - trigger.length;
		if (endsWithWordCharacter(text, triggerStart)) continue;

		// Strictly greater, so on a tie the earlier map entry wins — which makes the
		// case-insensitive pass deterministic when several entries differ only by case.
		if (matchedTrigger === null || trigger.length > matchedTrigger.length) {
			matchedTrigger = trigger;
			replacement = candidateReplacement;
		}
	}

	if (matchedTrigger === null || replacement === undefined) return null;

	return {
		trigger: matchedTrigger,
		replacement,
		start: text.length - matchedTrigger.length,
	};
}

// Trailing quotes and brackets are peeled before the sentence test, so
// ("...filed.") still reads as a finished sentence.
const CLOSING_MARKS = /["'’”)\]]+$/u;

// A period here doesn't end a sentence, so "Smith Corp. atty" stays lowercase.
const NON_TERMINAL_ABBREVIATIONS = [
	"inc.",
	"corp.",
	"ltd.",
	"llc.",
	"co.",
	"no.",
	"v.",
	"vs.",
	"mr.",
	"mrs.",
	"ms.",
	"dr.",
	"jr.",
	"sr.",
	"st.",
	"ave.",
	"et al.",
	"e.g.",
	"i.e.",
];

/**
 * Whether an expansion inserted here begins a sentence. Only an empty field or
 * a closing [.!?] qualifies — per APA a semicolon or colon continues the
 * sentence, and a bare line break is not sentence-ending punctuation.
 */
function isSentenceStart(textBefore: string): boolean {
	const trimmed = textBefore.replace(/\s+$/u, "");
	if (trimmed === "") return true;

	const core = trimmed.replace(CLOSING_MARKS, "");
	if (!/[.!?]$/u.test(core)) return false;

	const lower = core.toLowerCase();
	return !NON_TERMINAL_ABBREVIATIONS.some((a) => lower.endsWith(a));
}

// Opening marks are stepped over so ("memo" capitalizes the m, not nothing.
const OPENING_MARKS = /^["'“‘(\[]+/u;

/**
 * Uppercase the expansion's first letter, never lowercase it: the stored case is
 * the author's intent, so proper nouns like "Grocery Outlet, Inc." survive intact.
 */
function capitalizeFirstLetter(text: string): string {
	const skipped = text.match(OPENING_MARKS)?.[0].length ?? 0;
	const rest = [...text.slice(skipped)];
	if (rest.length === 0) return text;

	const upper = rest[0].toUpperCase();
	if (upper === rest[0]) return text;

	return text.slice(0, skipped) + upper + rest.slice(1).join("");
}

/**
 * Exact case wins, otherwise fall back to case-insensitive: a lone "tc" fires
 * however it was capitalized, while distinct "tc" and "Tc" entries each keep their
 * own expansion for the spelling that matches exactly.
 */
function resolveTriggerAtEnd(
	text: string,
	abbreviationMap: AbbreviationMap,
): TriggerMatch | null {
	return (
		findTriggerAtEnd(text, abbreviationMap, (value) => value) ??
		findTriggerAtEnd(text, abbreviationMap, (value) => value.toLowerCase())
	);
}

export function expandAbbreviationAtCursor({
	value,
	previousValue,
	cursorPosition,
	abbreviationMap,
}: ExpandAbbreviationAtCursorOptions): AbbreviationExpansionResult | null {
	if (!abbreviationMap?.size) return null;
	if (cursorPosition === null || cursorPosition === undefined) return null;
	if (value.length !== previousValue.length + 1) return null;

	const insertedCharacterIndex = cursorPosition - 1;
	if (insertedCharacterIndex < 0 || value[insertedCharacterIndex] !== " ") {
		return null;
	}

	const beforeTriggerSpace = value.slice(0, insertedCharacterIndex);
	const afterCursor = value.slice(cursorPosition);

	let match = resolveTriggerAtEnd(beforeTriggerSpace, abbreviationMap);
	let suffix = "";

	// Peel a possessive and/or trailing punctuation and retry. Only as a fallback: a
	// trigger may itself end in punctuation, and has to match as itself first.
	if (!match) {
		const punctuation = beforeTriggerSpace.match(TRAILING_PUNCT)?.[0] ?? "";
		const stem = punctuation
			? beforeTriggerSpace.slice(0, -punctuation.length)
			: beforeTriggerSpace;
		const possessive = stem.match(POSSESSIVE)?.[0] ?? "";
		const base = possessive ? stem.slice(0, -possessive.length) : stem;

		if (punctuation || possessive) {
			match = resolveTriggerAtEnd(base, abbreviationMap);
			if (match) suffix = possessive + punctuation;
		}
	}

	if (!match) return null;

	const textBeforeMatch = beforeTriggerSpace.slice(0, match.start);
	const replacement = isSentenceStart(textBeforeMatch)
		? capitalizeFirstLetter(match.replacement)
		: match.replacement;

	const newBefore = `${textBeforeMatch}${replacement}${suffix} `;

	return {
		value: newBefore + afterCursor,
		cursorPosition: newBefore.length,
		trigger: match.trigger,
	};
}
